/**
 * PGSS-5744: Settings, SKU Codes. Pure helpers, so the page's rules are tested without rendering it.
 * A stock product's SKU is COLOUR-TYPE[-SUBTYPE]-LxWxH[-POLISH], built from the codes on this page.
 */

export const PARTS = [
  { value: 'COLOUR', label: 'Colour' },
  { value: 'TYPE', label: 'Type (Shape)' },
  { value: 'SUB_TYPE', label: 'Sub type' },
  { value: 'POLISH', label: 'Polish' },
];

export function partLabel(part) {
  return PARTS.find((p) => p.value === part)?.label || part;
}

/** Admin and Super Admin can change a SKU and the code list (the backend allows the same two roles). */
export function canManageSku(user) {
  // `role` may be one name or a list (a user with several roles), as may `roles` (review of #2744).
  const names = [].concat(user?.role || [], (user?.roles || []).map((r) => (typeof r === 'string' ? r : r?.name)));
  return names.some((n) => ['admin', 'super admin'].includes(String(n || '').trim().toLowerCase()));
}

/** Entries of one part (or all), matching the search in name, type or code. */
export function filterCodes(codes, part, search) {
  const q = String(search || '').trim().toLowerCase();
  return (codes || []).filter(
    (c) =>
      (!part || c.part === part) &&
      (!q || [c.name, c.type_name, c.code].some((v) => String(v || '').toLowerCase().includes(q)))
  );
}

/**
 * The products ticked by default in Generate SKUs: those that get a SKU and share it with no other product.
 * Products that would share one are usually the same product entered twice; merge them first (PGSS-5745).
 */
export function defaultSelection(rows) {
  return (rows || []).filter((r) => r.sku && !(r.same_as || []).length).map((r) => r.product_id);
}

export function previewSummary(rows) {
  const list = rows || [];
  return {
    total: list.length,
    ready: list.filter((r) => r.sku && !(r.same_as || []).length).length,
    shared: list.filter((r) => r.sku && (r.same_as || []).length).length,
    unlisted: list.filter((r) => (r.unlisted || []).length).length,
    noSku: list.filter((r) => !r.sku).length,
  };
}

/** What to tell the Admin about one preview row, besides its SKU. */
export function rowNotes(row) {
  const notes = [];
  if (!row?.sku) notes.push(row?.no_sku_reason || 'No SKU');
  if ((row?.same_as || []).length) {
    notes.push(`Would share its SKU with ${row.same_as.length} other product(s): merge duplicates first`);
  }
  (row?.unlisted || []).forEach((u) => notes.push(`Not in the code list: ${u}`));
  if (row?.archived) notes.push('Archived');
  return notes;
}

/** The message of a refused save (400 with [{ field, message }]), or a fallback. */
export function errorMessage(error, fallback) {
  const data = error?.response?.data;
  if (Array.isArray(data) && data[0]?.message) return data.map((e) => e.message).join(' ');
  return data?.message || fallback;
}

/**
 * PGSS-5745: what merging a group sends: the copy kept, and every other copy that can be merged. A copy with stock
 * committed to open orders or on order from a supplier is left out (the backend refuses it too).
 */
export function mergeRequest(group, keepId) {
  return {
    keep_id: keepId,
    copy_ids: (group?.copies || []).filter((c) => c.product_id !== keepId && !c.blocked_reason).map((c) => c.product_id),
  };
}

/**
 * Review of #2744: what a merge is about to do, for its confirmation. The copy kept (and whether it is archived: a
 * merge then moves live stock onto an archived product), the copies archived, and the units moved.
 */
export function mergeSummary(group, request) {
  const copies = group?.copies || [];
  const keep = copies.find((c) => c.product_id === request?.keep_id);
  const merged = copies.filter((c) => (request?.copy_ids || []).includes(c.product_id));
  return {
    keepName: keep?.name || '',
    keepArchived: Boolean(keep?.archived),
    mergedNames: merged.map((c) => c.name),
    units: merged.reduce((sum, c) => sum + (c.units_in_stock || 0), 0),
  };
}

/**
 * Re-review of #2744: what a tab shows while its list loads. A failed load is its own state, never an empty list: an
 * empty Duplicates tab says "no stock product is entered twice", which must not be what a timeout looks like.
 */
export function loadState(value, error) {
  if (error) return 'error';
  return value === null || value === undefined ? 'loading' : 'ready';
}

